import { Area, Location } from '../CommonTypes';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { isMobile } from '../states/planner';

type SVGStyle = React.CSSProperties & {
  fill?: string;
  stroke?: string;
  opacity?: number | string;
  transform?: string;
};

export const getAnchor = (tooltipplace: string, svgArea: Area, tooltipSize: { width: number; height: number }) => {
  const anchor =
    tooltipplace === 'top'
      ? { x: svgArea.top.x - tooltipSize.width / 2, y: svgArea.top.y - tooltipSize.height - 2 }
      : tooltipplace === 'right'
        ? { x: svgArea.right.x + 1, y: svgArea.right.y - tooltipSize.height / 2 }
        : tooltipplace === 'bottom'
          ? { x: svgArea.bottom.x - tooltipSize.width / 2, y: svgArea.bottom.y + 1 }
          : {
              x: svgArea.left.x - tooltipSize.width - 1,
              y: svgArea.left.y - tooltipSize.height / 2,
            };
  return anchor;
};

export const CustomShapeArea = ({
  location,
  setArea,
  handleMouseLeave,
  onClick,
  openOnClick,
  anchorType,
  pathStyle,
  className,
  mobileScale,
  children,
}: {
  location: Location;
  setArea: (area: Area | null) => void;
  handleMouseLeave?: () => void;
  onClick?: (e: React.MouseEvent<SVGPathElement, MouseEvent>) => void;
  openOnClick?: boolean;
  anchorType?: 'line' | 'box';
  pathStyle?: SVGStyle;
  className?: string;
  mobileScale?: number;
  children?: ReactNode;
}) => {
  const groupRef = useRef<SVGGElement>(null);
  const pathRef = useRef<SVGPathElement>(null);
  const [debugPoints, setDebugPoints] = useState<Area | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const debug = false;

  const calculateAnchorPoints = () => {
    const path = pathRef.current;
    if (!path) return;

    const svg = path.ownerSVGElement;
    if (!svg) return;

    // Robust viewBox access
    const { x: vbMinX, y: vbMinY, width: vbWidth, height: vbHeight } = svg.viewBox.baseVal;

    const toPct = (x: number, y: number) => ({
      x: ((x - vbMinX) / vbWidth) * 100,
      y: ((y - vbMinY) / vbHeight) * 100,
    });

    // BBox in element's local (untransformed) space
    const bbox = path.getBBox();
    const centerX = bbox.x + bbox.width / 2;
    const centerY = bbox.y + bbox.height / 2;

    // Same transform you apply to the <path> on mobile
    const s = isMobile() && mobileScale ? mobileScale : 1;
    const applyMobileScale = (pt: { x: number; y: number }) =>
      s === 1
        ? pt
        : {
            x: centerX + (pt.x - centerX) * s,
            y: centerY + (pt.y - centerY) * s,
          };

    let left: { x: number; y: number } | null = null;
    let right: { x: number; y: number } | null = null;
    let top: { x: number; y: number } | null = null;
    let bottom: { x: number; y: number } | null = null;

    if (anchorType === 'box') {
      // Box anchor points from the unscaled bbox...
      left = { x: bbox.x, y: centerY };
      right = { x: bbox.x + bbox.width, y: centerY };
      top = { x: centerX, y: bbox.y };
      bottom = { x: centerX, y: bbox.y + bbox.height };
    } else {
      // Sample the path in unscaled space
      const tolY = vbHeight * 0.004;
      const tolX = vbWidth * 0.004;

      const total = (path as SVGGeometryElement).getTotalLength();
      const steps = 400;

      for (let i = 0; i <= steps; i++) {
        const pt = (path as SVGGeometryElement).getPointAtLength((total * i) / steps);

        if (Math.abs(pt.y - centerY) <= tolY) {
          if (!left || pt.x < left.x) left = { x: pt.x, y: pt.y };
          if (!right || pt.x > right.x) right = { x: pt.x, y: pt.y };
        }

        if (Math.abs(pt.x - centerX) <= tolX) {
          if (!top || pt.y < top.y) top = { x: pt.x, y: pt.y };
          if (!bottom || pt.y > bottom.y) bottom = { x: pt.x, y: pt.y };
        }
      }

      // Fallbacks if sampling missed
      left ??= { x: bbox.x, y: centerY };
      right ??= { x: bbox.x + bbox.width, y: centerY };
      top ??= { x: centerX, y: bbox.y };
      bottom ??= { x: centerX, y: bbox.y + bbox.height };
    }

    // ⭐ Apply the mobile transform to anchors so they match the visually scaled path
    const leftT = applyMobileScale(left);
    const rightT = applyMobileScale(right);
    const topT = applyMobileScale(top);
    const bottomT = applyMobileScale(bottom);

    return {
      left: toPct(leftT.x, leftT.y),
      right: toPct(rightT.x, rightT.y),
      top: toPct(topT.x, topT.y),
      bottom: toPct(bottomT.x, bottomT.y),
    };
  };

  const openTooltip = () => {
    const anchorPoints = calculateAnchorPoints();
    if (!anchorPoints) return;

    setDebugPoints({
      ...anchorPoints,
      reference_id: location.ref_id,
      coordinates: location.coordinates,
      tooltipplace: location.tooltipplace,
    });

    setArea({
      ...anchorPoints,
      reference_id: location.ref_id,
      coordinates: location.coordinates,
      title: location.title,
      text: location.text,
      tooltipplace: location.tooltipplace,
    });

    setIsOpen(true);
  };

  useEffect(() => {
    if (!mobileScale) return;
    const handleScale = () => {
      const paths = groupRef.current?.getElementsByTagName('path');
      if (!paths) return;

      Array.from(paths).forEach(path => {
        if (path) {
          const bbox = path.getBBox();
          const centerX = bbox.x + bbox.width / 2;
          const centerY = bbox.y + bbox.height / 2;

          if (isMobile()) {
            path.setAttribute(
              'transform',
              `translate(${centerX} ${centerY}) scale(${mobileScale}) translate(${-centerX} ${-centerY})`
            );
          } else {
            path.setAttribute('transform', 'scale(1)');
          }
        }
      });
    };

    handleScale(); // Call initially to set the correct state

    window.addEventListener('resize', handleScale);
    return () => window.removeEventListener('resize', handleScale);
  }, []);

  // Close on outside tap/click while open (helps mobile where blur can be unreliable)
  useEffect(() => {
    if (!isOpen) return;

    const onDocPointerDown = (ev: PointerEvent) => {
      const g = groupRef.current;
      if (g && ev.target instanceof Node && !g.contains(ev.target)) {
        closeTooltip();
      }
    };

    // capture phase catches events before they reach the SVG
    document.addEventListener('pointerdown', onDocPointerDown, true);
    return () => document.removeEventListener('pointerdown', onDocPointerDown, true);
  }, [isOpen]);

  const closeTooltip = () => {
    setIsOpen(false);
    setArea(null);
    handleMouseLeave?.();
  };

  const handleLeave = () => {
    if (isMobile()) return;
    closeTooltip();
  };

  const handleEnter = () => {
    if (isMobile()) return; // no hover on touch
    openTooltip();
  };

  const handleOnClick = (e: React.MouseEvent<SVGPathElement, MouseEvent>) => {
    if (openOnClick) {
      if (isOpen) {
        closeTooltip();
      } else {
        openTooltip();
        groupRef.current?.focus();
      }
    }
    onClick?.(e);
  };

  const handleBlur = (e: React.FocusEvent<SVGGElement>) => {
    // If focus moves outside this group, close
    const g = groupRef.current;
    const next = e.relatedTarget as Node | null;
    if (!g || !next || !g.contains(next)) {
      closeTooltip();
    }
  };

  return (
    <g
      ref={groupRef}
      id={location.id}
      className={['hover:cursor-pointer', className].join(' ')}
      onBlur={handleBlur}
      // pointer/mouse interactions
      onClick={handleOnClick}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    >
      <path ref={pathRef} d={location.coordinates} style={pathStyle} />
      {children}
      {debugPoints && debug && (
        <>
          <circle cx={`${debugPoints.left.x}%`} cy={`${debugPoints.left.y}%`} r="10" fill="red" />
          <circle cx={`${debugPoints.right.x}%`} cy={`${debugPoints.right.y}%`} r="10" fill="red" />
          <circle cx={`${debugPoints.top.x}%`} cy={`${debugPoints.top.y}%`} r="10" fill="red" />
          <circle cx={`${debugPoints.bottom.x}%`} cy={`${debugPoints.bottom.y}%`} r="10" fill="red" />
        </>
      )}
    </g>
  );
};
